<script setup lang="ts">
import { computed, reactive, ref, watchEffect, nextTick, onMounted, watch } from 'vue'
import { useWorkOrderChat } from '@/composables/rc/work-order-chat'
import { OrderBy, type IFilters } from '@/types/shared/filters'
import { useOIDCStore } from '@/stores/oidc.store'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import type { IWorkOrderChatMessageForm } from '@/types/rc/forms'
import type { IWorkOrderChatMessage } from '@/types/rc/work-orders'
import { ArrowUp, Loading } from '@element-plus/icons-vue'
import { DateTime } from 'luxon'
import { Icon } from '@iconify/vue'

const { profile } = useOIDCStore()

const props = defineProps<{ workOrderNumber: string }>()

// Initialize with the requested page size
const filters = reactive<IFilters>({
  page: 1,
  per_page: 25,
  order_by: 'sent_at',
  order_by_order: OrderBy.DESC,
})

const { createWorkOrderChatMessageMutation, workOrderChatMessagesQuery } = useWorkOrderChat(props.workOrderNumber)

// Initial query to get the last messages
const { isPending, data, isError, isFetching, refetch, error: queryError } = workOrderChatMessagesQuery(filters)
const chatMessages = ref<IWorkOrderChatMessage[]>([])
const isLoadingMore = ref<boolean>(false)
const hasMoreMessages = ref<boolean>(true)
const newMessage = ref<string>('')

// Reference to the messages container for scroll management
const messagesRef = ref<HTMLElement | null>(null)
const messageInputRef = ref<InstanceType<typeof import('element-plus').ElInput> | null>(null)

// Store scroll position before updates
const scrollPositionBeforeLoad = ref<number>(0)
const scrollHeightBeforeLoad = ref<number>(0)
const isAutoScrollEnabled = ref<boolean>(true)
const isInitialLoad = ref<boolean>(true)

// Watch for changes in the data and update chatMessages
watchEffect(() => {
  if (data.value?.data) {
    if (filters.page === 1) {
      // For initial load or refresh, replace messages
      chatMessages.value = [...data.value.data]

      // Reset scroll flags
      isAutoScrollEnabled.value = true

      // After initial data load, scroll to bottom
      if (isInitialLoad.value) {
        isInitialLoad.value = false
        nextTick(() => {
          scrollToBottom()
        })
      }
    } else {
      // For pagination (older messages), prepend new messages to existing ones
      // Get the list of new messages
      const oldMessages = data.value.data.filter(
        (newMsg) => !chatMessages.value.some((existingMsg) => existingMsg.id === newMsg.id)
      )

      // Prepend old messages to the existing list
      chatMessages.value = [...oldMessages, ...chatMessages.value]
    }

    // Check if we've loaded all available messages
    hasMoreMessages.value = data.value.page < data.value.total_pages

    // If we're loading older messages, maintain scroll position
    if (filters.page > 1) {
      nextTick(() => {
        if (messagesRef.value) {
          // Calculate new position to maintain the same view after loading older messages
          const newScrollHeight = messagesRef.value.scrollHeight
          const scrollDifference = newScrollHeight - scrollHeightBeforeLoad.value
          messagesRef.value.scrollTop = scrollDifference + scrollPositionBeforeLoad.value
        }
      })
    }

    // Reset loading state
    isLoadingMore.value = false
  }
})

// Keep scrolling to bottom when new messages are added
watch(
  () => data.value,
  (newData) => {
    if (newData && filters.page === 1) {
      // Only scroll to bottom for the first page (most recent messages)
      // and not when loading older messages
      nextTick(() => {
        scrollToBottom()
      })
    }
  }
)

const { isPending: createIsPending, mutateAsync: createMessageMutate } = createWorkOrderChatMessageMutation
const isSendDisabled = computed(() => {
  return !newMessage.value.trim()
})

// Load older messages when scrolling to top
const handleScroll = () => {
  if (!messagesRef.value) return
  if (!data.value) return
  if (isLoadingMore.value) return
  if (!hasMoreMessages.value) return

  // If we're near the top of the scroll container (within 50px)
  if (messagesRef.value.scrollTop < 50) {
    // Save current scroll information before loading
    scrollPositionBeforeLoad.value = messagesRef.value.scrollTop
    scrollHeightBeforeLoad.value = messagesRef.value.scrollHeight

    // Load previous page of messages
    isLoadingMore.value = true
    filters.page = filters.page + 1

    // When scrolling up to load older messages, disable auto-scroll
    isAutoScrollEnabled.value = false
  }
}

// Utility function to scroll to bottom of messages
const scrollToBottom = () => {
  if (messagesRef.value) {
    messagesRef.value.scrollTop = messagesRef.value.scrollHeight
  }
}

const sendMessage = async () => {
  if (isSendDisabled.value) return

  const form: IWorkOrderChatMessageForm = {
    message: newMessage.value,
  }

  await createMessageMutate(form, {
    onSuccess: () => {
      newMessage.value = ''
      // Enable auto-scroll after sending a message
      isAutoScrollEnabled.value = true

      // Reset to page 1 to get latest messages
      filters.page = 1
    },
  })
}

const formatChatDate = (isoDate: string) => {
  return DateTime.fromISO(isoDate).toFormat('MM/dd/yyyy HH:mm')
}

// Set up the scroll event listener after component mount
onMounted(() => {
  nextTick(() => {
    if (messagesRef.value) {
      // Initial scroll to bottom to show latest messages
      scrollToBottom()
    }
    // Focus the input field after component is mounted
    if (messageInputRef.value) {
      // For element-plus textarea, we need to access the native textarea element
      messageInputRef.value.focus()
    }
  })
})
</script>

<template>
  <template v-if="isError && !isFetching">
    <rl-error-result :error="queryError">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
  </template>
  <template v-else>
    <div v-if="isPending && isInitialLoad" class="text-center text-tertiary py-8">Loading messages...</div>

    <div class="flex flex-col h-[60vh]">
      <div v-if="isLoadingMore" class="flex items-center justify-center py-2 text-tertiary text-xs gap-2">
        <el-icon class="animate-spin"><Loading /></el-icon> Loading older messages...
      </div>

      <div v-if="data?.total_pages == 0" class="flex justify-center items-center">
        <RlEmptyDataset title="This conversation has no messages" />
      </div>

      <div
        v-if="hasMoreMessages && !isLoadingMore && !isPending"
        class="flex items-center justify-center py-2 text-tertiary text-xs gap-2"
      >
        <el-icon><ArrowUp /></el-icon>
        <span>Scroll up for older messages</span>
      </div>

      <div ref="messagesRef" class="flex-1 overflow-y-auto p-4 flex flex-col gap-3" @scroll="handleScroll">
        <div
          v-for="message in chatMessages"
          :key="message.id"
          :class="['max-w-[70%] mb-2', message.user.id === profile?.id ? 'self-end' : 'self-start']"
        >
          <div
            :class="[
              'p-2 px-3 rounded-lg',
              message.user.id === profile?.id
                ? 'bg-component-fill-blue text-text-primary'
                : 'bg-component-fill-emerald text-text-primary',
            ]"
          >
            <span class="block text-[0.8rem] text-text-tertiary">{{ message.user.name }}</span>
            <p class="whitespace-pre-wrap m-0 mb-1 break-words">{{ message.message }}</p>
            <span class="block text-[0.7rem] text-text-tertiary text-right">{{ formatChatDate(message.sent_at) }}</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-spacing-sm mt-4 px-4 pb-4">
        <el-input
          ref="messageInputRef"
          v-model="newMessage"
          type="textarea"
          placeholder="Type your message..."
          :disabled="isPending || createIsPending"
          :autosize="{ minRows: 2, maxRows: 4 }"
          @keyup.enter.ctrl="sendMessage"
        />
        <div class="flex justify-between items-center mt-2">
          <small class="text-tertiary">Press Ctrl+Enter to send</small>
          <div>
            <el-button type="primary" :disabled="isSendDisabled" :loading="createIsPending" @click="sendMessage"
              >Send</el-button
            >
            <el-button
              link
              title="Refresh"
              type="primary"
              :disabled="isFetching || isPending || createIsPending"
              @click="refetch()"
            >
              <Icon icon="material-symbols:refresh" height="16" :inline="true" />
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>
